import type { Dictionary } from "./types";

const zh: Dictionary = {
  appName: "LK Storekeeper",
  login: {
    subtitle: "请使用管理员或仓库管理员账号登录。",
    username: "用户名",
    password: "密码",
    signIn: "登录",
    signingIn: "正在登录...",
    loginFailed: "登录失败",
  },
  header: {
    logout: "退出登录",
    language: "语言",
    theme: "主题",
    themeLight: "浅色",
    themeDark: "深色",
    themeSystem: "跟随系统",
    session: "会话",
    sessionAccess: "访问令牌",
    sessionRefresh: "登录有效期",
    sessionExpired: "已过期",
    networkDebug: "网络调试",
    otherStatuses: "其他状态",
    shortReceiptNumbers: "委托单号不含 /N",
    allPages: "所有页面",
  },
  online: {
    label: "在线用户",
    online: "在线",
    offline: "离线",
    active: "正在使用",
    idle: "标签页在后台",
    empty: "当前无人在线",
    you: "你",
    never: "从未登录",
    loading: "加载中…",
  },
  tabs: {
    ui: "界面",
    db: "数据库",
  },
  tablesIndex: {
    heading: "数据表",
    editable: "可编辑",
    columns: (count) => `${count} 个字段`,
    notFound: "数据库中未找到该表",
  },
  tableView: {
    addRow: "新增记录",
    edit: "编辑",
    noRows: "暂无数据。",
    pageInfo: (page, totalPages, total) => `第 ${page} / ${totalPages} 页（共 ${total} 条）`,
    previous: "上一页",
    next: "下一页",
    addTitle: "新增记录",
    editTitle: "编辑记录",
    cancel: "取消",
    save: "保存",
    saving: "保存中...",
    loading: "加载中...",
    selectPlaceholder: "— 请选择 —",
    loadingOptions: "加载选项中...",
    optionsLoadFailed: "加载选项失败",
    searchPlaceholder: "搜索...",
    noSearchResults: "未找到匹配项",
    searchTable: "搜索...",
  },
  errors: {
    loadFailed: "加载数据表失败",
    saveFailed: "保存失败",
    deleteFailed: "删除失败",
    serverError: "服务器出错。请重试；如果问题持续，请联系管理员。",
    backendUnreachable: "无法连接仓库服务器。请检查网络后稍后重试。",
    duplicate: "已存在相同的记录。",
    stillReferenced: "该记录正被其他数据使用，无法删除或修改。",
    missingReference: "未找到关联记录，可能已被删除。",
    requiredField: "必填字段为空。",
    integrityViolation: "此更改与现有数据冲突。",
  },
  units: {
    kg: "kg",
    m3: "m³",
    kgM3: "kg/m³",
  },
  confirm: {
    yes: "是",
    no: "否",
    confirm: "确认",
    cancel: "取消",
    close: "关闭",
    discardTitle: "放弃更改？",
    discardMessage: "此表单尚未提交。如果现在关闭，已输入的数据将会丢失。",
    discardConfirm: "放弃",
    keepEditing: "继续编辑",
  },
  workflow: {
    receiptPicker: {
      heading: "委托单",
      placeholder: "— 请选择委托单 —",
      newButton: "新建委托单",
      newTitle: "新建委托单",
      orderNumber: "订单号",
      cargoName: "货物名称",
      warehouse: "仓库",
      isOurFirstMile: "我方头程",
      plannedWeightKg: "计划重量（公斤）",
      plannedVolumeM3: "计划体积（立方米）",
      plannedFigure: "计划值（来自发货人）",
      actualFigure: "当前余量（收货减去发货/报废）",
      orderNumberHint: (max) => `最多 ${max} 个字符。`,
      create: "创建",
      creating: "创建中...",
      noReceipts: "暂无委托单。",
      editButton: "编辑委托单",
      editTitle: "编辑委托单",
      sequenceNumber: "订单内委托单序号",
      renumberWarning: (from, to) =>
        `${from} 将在所有位置改为 ${to}，包括包装规格代码和操作历史。之前打印的标签将不再匹配。`,
      save: "保存",
      saving: "保存中...",
      saved: "更改已保存。",
      cargoNameOriginal: "货物名称（原文）",
      cargoNameRu: "货物名称（俄文）",
      cargoNameEn: "货物名称（英文）",
      cargoNameZh: "货物名称（中文）",
      cargoNameTranslations: "货物名称翻译",
      totalPackageCount: "总件数",
      cargoReadyOn: "货物备妥日期",
      selectedLabel: (receiptNumber) => `委托单 ${receiptNumber}`,
      scanButton: "扫描订单二维码",
      scanTitle: "扫描订单二维码",
      scanHint: "将摄像头对准订单的二维码。",
      scanLoading: "正在启动摄像头...",
      scanCameraError: "无法访问摄像头，请检查权限后重试。",
      scanMatchesTitle: "选择委托单",
      scanNotFound: (orderNumber) => `未找到订单 ${orderNumber} 对应的委托单。`,
      shareButton: "分享委托单",
      shareTitle: "分享委托单",
      shareCopyReceipt: "复制委托单编号",
      shareCopyLink: "复制链接",
      shareCopied: "已复制！",
      shareQrHint: "扫描以打开此委托单。",
      supplier: "发货人",
      supplierPlaceholder: "— 选择发货人 —",
      supplierSearchPlaceholder: "搜索发货人...",
      supplierNoResults: "未找到发货人。",
      supplierNone: "无发货人",
      supplierAddAnother: "添加发货人",
      supplierRemove: "移除发货人",
      trackingNumbers: "追踪号",
      warehouseArrivalOn: "到达日期",
      createdBy: "创建者",
      statusLabel: "状态",
      commentLabel: "备注",
      commentPlaceholder: "为此运单添加备注…",
      statusNames: {
        awaiting_arrival: "等待到货",
        partially_received: "部分到货",
        received: "已到货",
        scheduled_for_trip: "已安排发运",
        partially_shipped: "部分发运",
        shipped: "已发运",
      },
    },
    steps: {
      main: "主要",
      operations: "仓库操作",
      packageTypes: "包装规格",
      receiving: "入库",
      shipping: "出库/报废",
      transfer: "调拨",
      balances: "库存",
      connections: "关联",
      files: "文件",
      chat: "聊天",
      more: "更多",
      arrivals: "预计到货",
      services: "增值服务",
    },
    main: {
      receiptFields: "委托单详情",
      cargoName: "货物名称",
      plannedWeightKg: "计划重量 (kg)",
      plannedVolumeM3: "计划体积 (m³)",
      actualVolumeM3: "实际体积 (m³)",
      totalPackageCount: "总件数",
      packageTypesHeading: "包装规格",
      colDimensions: "包装规格",
      colCode: "编码",
      colPackaging: "包装类型",
      colCount: "件数",
      colWeight: "重量",
      colLocation: "库位",
      colReceivedOn: "收货日期",
      printSticker: "打印箱贴",
      openReceiving: "收货",
      openShipping: "报损",
      noPackageTypes: "此委托单还没有包装规格。",
      toggleBreakdown: "按库位显示",
      warehouseHeading: "仓库",
      warehouseDescription: "此委托单的收货、发货和包装规格。",
      colVolume: "体积",
    },
    receiptHeader: {
      date: "创建日期",
      manager: "经理",
      managerPlaceholder: "未指定",
      planHeading: "计划货物参数",
      acceptedHeading: "在库",
      packages: "件数",
      weight: "重量",
      volume: "体积",
      density: "SF",
      cargoReadyOn: "货物备妥日期",
      receivedOn: "到仓日期",
      comment: "备注",
      commentPlaceholder: "为此运单添加备注…",
      saveComment: "保存",
      savingComment: "正在保存…",
      savedComment: "已保存",
    },
    arrivals: {
      heading: "预计到货",
      tag: "我们的第一公里",
      empty: "该收货单没有预计到货记录。",
      ourFirstMile: "我方头程",
      supplier: "发货人",
      addressAndContacts: "地址和联系方式",
      contacts: "联系方式",
      trackingNumbers: "追踪号",
      plannedArrival: "计划到达日期",
      plannedPackages: "计划件数",
      plannedWeight: "计划重量",
      plannedVolume: "计划体积",
      arrivedOn: "实际到达日期",
      deleteSupplier: "删除发货人",
      deleteTitle: "删除发货人？",
      deleteBody: (supplier) =>
        supplier
          ? `发货人“${supplier}”将从此运单中移除。`
          : "将删除这条没有发货人的预计到货记录。",
      deleteWarning: "该行的跟踪号和计划到达日期也会一并删除。",
    },
    services: {
      heading: "增值服务",
      placeholder: "增值服务功能即将推出。",
      palletizing: "打托",
      marking: "贴标",
      soon: "即将推出",
    },
    operationsLog: {
      colPackages: "件数",
      colWeight: "重量",
      colVolume: "体积",
      heading: "仓库操作",
      description: "此委托单的所有操作。",
      empty: "此委托单还没有操作。",
      colType: "类型",
      colNumber: "ID",
      colDate: "日期",
      colComment: "备注",
      typeReceiving: "收货",
      typeShipping: "发货",
      typeTransfer: "移库",
      typeReceiptTransfer: "结转",
      detailTitle: (type, id, date) => `${type} ${id}（${date}）`,
      detailColPackageType: "包装规格",
      detailColCount: "件数",
      detailColWeight: "重量",
      detailColLocation: "库位",
      receiptDocument: (number) => `收据 ${number}`,
      shippingOrder: (number) => `发运单 ${number}`,
    },
    boxSticker: {
      title: "箱贴",
      printOne: "打印",
      printAll: "全部打印",
      empty: "暂无可标记的件——请先进行收货操作。",
      labelReceipt: "委托单",
      labelOrder: "订单",
      labelCode: "编码",
      labelDimensions: "长*宽*高",
      labelPlace: "件",
      orientation: "方向",
      vertical: "竖版",
      horizontal: "横版",
      generating: "正在生成箱贴…",
      generatingOne: "正在生成…",
      progress: (done, total) => `${done} / ${total}`,
      ready: "箱贴已生成",
      openPdf: "打开 PDF",
      failed: "箱贴生成失败",
    },
    receiptOverview: {
      shownOf: (shown, total) => `显示 ${shown} / ${total}`,
      loadMore: "加载更多",
      heading: "预计到货",
      loading: "正在加载预计到货...",
      empty: "仓库内暂无预计到货。",
      receiptNumber: "委托单号",
      cargo: "货物",
      supplier: "发货人",
      supplierAddress: "地址",
      supplierPhone: "电话",
      supplierEmail: "电子邮箱",
      cargoReadyOn: "备妥",
      receivedOn: "到仓",
      arrivalOn: "到达",
      trackingNumbers: "跟踪单号",
      trackingContacts: "跟踪联系人",
      contactsDialogTitle: (receipt: string) => `${receipt} 的联系人`,
      copy: "复制",
      copied: "已复制",
      packagesShort: "件数",
      weightShort: "重量",
      volumeShort: "体积",
      sfShort: "SF",
      originalName: (name) => `原文：${name}`,
      createdBy: "创建者",
      plannedPackages: "计划件数",
      plannedWeight: "计划重量",
      plannedVolume: "计划体积",
      shippedPackages: "已发件数",
      shippedWeight: "已发重量",
      shippedVolume: "已发体积",
      planCaption: "计划",
      shippedCaption: "已发",
      supplierShippedOn: "发货日期",
      plannedArrivalOn: "计划到仓",
      arrivalFrom: "计划到仓 自",
      arrivalTo: "至",
      datePlaceholder: "年.月.日",
      overdueOnly: "仅显示逾期",
      resetFilters: "重置",
      filteredEmpty: "所选期间内没有计划到仓的收货单。",
      overdue: "已逾期",
      comment: "备注",
    },
    landing: {
      tripBanner: (count: number) => `${count} 张发货单待发货`,
      tripBannerAction: "选择车次发货单并发货",
      expectedTab: "预计",
      inWarehouseTab: "在库",
      otherTab: "其他状态",
    },
    inWarehouse: {
      heading: "已入库",
      loading: "正在加载库存...",
      empty: "仓库暂无库存货物。",
      receiptNumber: "委托单号",
      cargo: "货物名称",
      packages: "件数",
      weight: "重量",
      volume: "体积",
      sf: "SF",
      acceptedOn: "入库日期",
      status: "货物状态",
      daysInWarehouse: "在库天数",
      comment: "备注",
      days: (count) => `${count} 天`,
    },
    packageTypes: {
      heading: "包装规格",
      code: "编码",
      dimensionsLabel: "尺寸（长*宽*高）",
      lengthCm: "长（厘米）",
      widthCm: "宽（厘米）",
      heightCm: "高（厘米）",
      add: "新增包装规格",
      adding: "添加中...",
      addToggle: "添加包装类型",
      cancel: "取消",
      empty: "该委托单暂无包装规格。",
      selectReceiptFirst: "请先在上方选择委托单。",
      delete: "删除",
      deleting: "删除中...",
      deleteConfirmTitle: "删除包装规格",
      deleteConfirmMessage: (code) => `确定删除包装规格 ${code}？`,
      deleteConfirmWarning: "此操作不可撤销。",
    },
    expectedArrivals: {
      heading: "预计到货",
      loading: "正在加载预计到货...",
      empty: "请先在上方选择委托单。",
      orderNumber: "订单号",
      cargoType: "货物类型",
      itemCount: "件数",
      packageCount: "包裹数",
      plannedWeightKg: "计划重量（千克）",
      plannedVolumeM3: "计划体积（立方米）",
      sf: "SF（千克/立方米）",
      trackingNumbers: "跟踪单号",
      trackingContacts: "跟踪联系人",
      supplierShippedOn: "发货日期",
      arrivalOn: "预计到达日期",
      cargoReadyOn: "货物备妥日期",
      status: "状态",
      notes: "备注",
    },
    operationUser: "操作人",
    operationUserUnknown: "未记录",
    conditions: {
      help: "说明",
      emptyCatalog: "列表为空。",
      handlingTitle: "操作条件",
      handlingAdd: "设置操作条件",
      handlingEdit: "修改操作条件",
      cargoTitle: "收货时货物实际状态",
      cargoHint: "可多选。悬停或点按「?」查看说明。",
      cargoEdit: "修改",
      cargoNone: "未指定",
    },
    lines: {
      heading: "明细行",
      packageType: "包装规格",
      packagingType: "包装类型",
      noPackagingTypes: "包装类型列表为空。",
      location: "库位",
      fromLocation: "源库位",
      toLocation: "目标库位",
      packageCount: "件数",
      actualWeightKg: "实际重量（公斤）",
      addLine: "添加明细行",
      removeLine: "删除",
      removeLineConfirm: "删除此明细行？已输入的数据将丢失。",
      noPackageTypes: "请先添加包装规格。",
      newPackageTypeOption: "➕ 新建包装规格",
      noLocations: "暂无可用库位。",
      missingPackageType: "请为每行选择包装规格。",
      missingPackagingType: "请为每行选择包装类型。",
      missingLocation: "请为每行选择库位。",
      missingWeight: "请为每行输入实际重量。",
      capacityPcs: "容量（件）",
      capacityValue: (pcs: number) => `容量：${pcs} 件`,
      missingCapacity: "请为每个托盘行输入容量（件），须为不小于 1 的整数。",
      locationUnspecified: "未指定",
    },
    receiving: {
      heading: "入库",
      description: "记录实际入库的货物件数。",
      modeAssistant: "入库创建助手",
      modeReceiving: "入库",
      modeAssistantHint: "创建包装规格并办理入库：可在此直接填写新件的尺寸。",
      modeReceivingHint: "仅按已创建的包装规格办理入库。",
      noPackageTypesForReceiving:
        "此委托单还没有包装规格。请先通过「入库创建助手」创建。",
      operationDate: "入库日期和时间",
      comment: "备注",
      commentPlaceholder: "收货备注（可选）…",
      newPackageType: "新建包装规格",
      newPackageTypeHint: "填写长×宽×高，包装规格将添加到本委托单。",
      addPackageType: "添加包装规格",
      addingPackageType: "添加中...",
      photos: "入库照片",
      photosHint: "照片将附加到委托单，并显示在「文档」标签页中。",
      photoGuideTitle: "照片要求",
      photoGuideItems: [
        {
          term: "整批外观",
          detail: "全部放在托盘/垫板上，体现体积和码放方式。",
        },
        {
          term: "每一件",
          detail: "2–3 个角度，可见包装完整性。",
        },
        {
          term: "标记",
          detail: "特写，文字无需放大即可辨认。",
        },
        {
          term: "破损",
          detail: "远景、中景、特写（附带尺子）。",
        },
        {
          term: "码放与固定",
          detail: "正面/侧面/顶部视图，缠膜、绑带、衬垫。",
        },
        {
          term: "单据",
          detail: "信息清晰可读；单据与货物合影。",
        },
      ],
      photoGuideNote: "重要：光线充足，正对拍摄。",
      addPhotos: "拍照",
      uploadingPhotos: "上传中...",
      photoCount: (count) => `${count} 张照片`,
      removePhoto: "删除照片",
      readOnly: "货物入库记录不可编辑。",
      submit: "提交入库",
      submitting: "提交中...",
      success: "入库操作已提交。",
      recent: "最近的入库操作",
      empty: "暂无入库操作。",
      total: "合计",
      confirmTitle: "确认入库",
      confirmMessage: "提交入库操作？",
      arrivalResultQuestion: "实收货物与计划不符。收货单应设为什么状态？",
      arrivalResultArrived: "已到货",
      arrivalResultPartial: "部分到货",
      arrivalResultAuto: "货物与计划一致——收货单将设为“已到货”。",
    },
    shipping: {
      heading: "出库/报废",
      description: "记录出库或报废的货物件数。",
      operationDate: "出库日期和时间",
      comment: "备注",
      commentPlaceholder: "发货备注（可选）…",
      reason: "出库类型",
      reasonDeparture: "发货",
      reasonWriteOff: "报废",
      submit: "提交出库",
      submitting: "提交中...",
      success: "出库操作已提交。",
      recent: "最近的出库操作",
      empty: "暂无出库操作。",
      total: "合计",
      confirmTitle: "确认出库",
      confirmMessage: "提交出库操作？",
      noStock: "该收货单没有库存，无可报损货物。",
      notInStock: "某行所选规格和库位没有库存。",
      exceedsPlaces: (code, available) => `${code}：件数超过库存（可用 ${available}）。`,
      exceedsWeight: (code, available) => `${code}：重量超过库存（可用 ${available} kg）。`,
      available: (places, weight) => `可用：${places} 件，${weight} kg`,
      departureHeading: (orderNumber) => `按发运单 ${orderNumber} 出库`,
      writeOffHeading: "报损",
      departureDescription: "已按各库位库存填写明细。请核对：发运单只能整单执行。",
      writeOffDescription: "货物报损（损坏、丢失）。出库只能按发运单办理。",
      loadingDraft: "正在按库存填写明细…",
      shortagesTitle: "库存不足，无法发运该单：",
      shortage: (code, requestedPlaces, availablePlaces, requestedKg, availableKg) =>
        `${code}：需要 ${requestedPlaces} 件 / ${requestedKg} kg，可用 ${availablePlaces} 件 / ${availableKg} kg`,
      notWhole: (code, places, kg, gotPlaces, gotKg) =>
        `发运单只能整单执行：${code} 需要 ${places} 件 / ${kg} kg，表单中为 ${gotPlaces} 件 / ${gotKg} kg。`,
      submitDeparture: "过账出库",
      submitWriteOff: "过账报损",
      confirmDeparture: "确认出库？之后发运单即视为已执行，不能再修改。",
      confirmWriteOff: "确认报损？",
      confirmTitleWriteOff: "确认报损",
      successWriteOff: "报损已提交。",
    },
    shippingOrders: {
      allOrders: "所有发货单 — 车次发货",
      heading: "发运单",
      description: "在 1C 组建车次时创建。货物只能按发运单整单发运。",
      empty: "暂无发运单。",
      ship: "发运",
      executed: (date) => `已执行 ${date}`,
      pending: "待发运",
      createdBy: "创建人",
      showStock: "各库位库存",
      noStock: "无库存",
      create: "创建发运单",
      createTitle: "新发运单（临时）",
      createHint: "临时按钮：发运单应来自 1C。发运单不得超出可用库存。",
      weightKg: "重量（kg）",
      free: (places, weight) => `可用：${places} 件，${weight} kg`,
      noFreeStock: "没有可用库存：货物已全部在发运单中或尚未入库。",
    },
    deletionMark: {
      marked: "已标记删除",
      filterLabel: "删除标记",
      filterAll: "全部",
      filterUnmarked: "未标记",
      filterMarked: "已标记删除",
      mark: "标记删除",
      unmark: "取消删除标记",
      markConfirmTitle: "删除标记",
      markConfirmMessage: (number) =>
        `将委托单 ${number} 标记删除？其将变为未过账，并带标记保留在列表中。只有没有仓库操作的委托单可以标记。`,
      unmarkConfirmMessage: (number) => `取消委托单 ${number} 的删除标记？其将重新过账。`,
      readOnly: "该委托单已标记删除：不能再办理新操作。",
    },
    transfer: {
      heading: "调拨",
      description: "在库位之间调拨货物。",
      occurredAt: "日期和时间",
      submit: "提交调拨",
      submitting: "提交中...",
      success: "调拨操作已提交。",
      recent: "最近的调拨操作",
      empty: "暂无调拨操作。",
      total: "合计",
      confirmTitle: "确认调拨",
      confirmMessage: "提交调拨操作？",
    },
    balances: {
      heading: "库存余额",
      description: "根据所选委托单的库存台账计算得出。",
      refresh: "刷新",
      location: "库位",
      packageType: "包装规格",
      balance: "余额",
      weightBalance: "重量（公斤）",
      empty: "该委托单暂无库存变动。",
      sfHeading: "SF",
      sfDescription:
        "在表单上计算：实际重量（来自入库）÷ 体积（包装规格尺寸 × 可用余额），单位 kg/m³。不可编辑。",
      sfWeight: "重量，kg",
      sfVolume: "体积，m³",
      sf: "SF，kg/m³",
      sfTotal: "合计",
      sfEmpty: "需要实际重量和包装规格尺寸才能计算。",
    },
    connections: {
      heading: "关联",
      description: "展示该委托单的货物流转：入库到库位、库位间调拨，以及出库/报废。",
      legendReceived: "已入库",
      legendTransferred: "已调拨",
      legendShipped: "已出库/报废",
      legendReceiptTransfer: "收货单转移",
      receiptTransferOperations: "收货单转移操作",
      receiptTransferOperationLines: "收货单转移操作明细",
      sinkDeparture: "已发货",
      sinkWriteOff: "已报废",
      empty: "请先为该委托单添加包装规格以查看关联。",
      breakdown: {
        heading: "全部库存变动明细",
        date: "日期",
        type: "类型",
        packageType: "包装规格",
        from: "来源",
        to: "去向",
        count: "件数",
        typeIn: "入库",
        typeOut: "出库/报废",
        typeTransfer: "调拨",
        receiptTransferOutPrefix: "转出至收货单",
        receiptTransferInPrefix: "转入自收货单",
        empty: "该委托单暂无已提交的库存变动。",
      },
    },
    fileDrop: {
      hint: "松开即可添加文件",
      unsupported: (names) => `不支持的文件格式：${names}`,
    },
    documents: {
      heading: "文件",
      description: "附加到此委托单的照片和文件。",
      formats: "支持的格式：JPG, PNG, WEBP, HEIC, PDF, DOC, DOCX, XLS, XLSX。",
      addPhotos: "添加照片",
      addFiles: "添加文件",
      uploading: "上传中...",
      empty: "暂无文件。",
      selectReceiptFirst: "请先在上方选择一张委托单。",
      download: "打开",
      delete: "删除",
      deleteConfirmTitle: "删除文档",
      deleteConfirmMessage: "确定删除此文档吗？此操作无法撤销。",
      storageDisabled: "服务器未配置文件存储。",
      tooLarge: "文件过大。",
      uploadedOn: "上传于",
      imageLoadError: "图片加载失败",
      saveFile: "下载",
      previewError: "无法打开表格，请下载文件查看。",
      previewEmpty: "此工作表为空。",
      previewTruncated: (shown: number, total: number) => `仅显示前 ${shown} 行，共 ${total} 行。`,
    },
    chat: {
      heading: "聊天",
      description: "关于此委托单的讨论。",
      receiptHeading: "运单聊天 ",
      selectReceiptFirst: "请先在上方选择一张委托单。",
      empty: "暂无消息，开始对话吧。",
      loading: "正在加载消息...",
      placeholder: "输入消息...",
      send: "发送",
      sending: "发送中...",
      attachPhoto: "附加照片",
      attachFile: "附加文件",
      systemBadge: "系统",
      you: "你",
      unknownSender: "未知发送者",
      imageLoadError: "图片加载失败",
      messageActions: "消息操作",
      copyMessage: "复制消息",
      replyTo: "回复此消息",
      replyingTo: "正在回复",
      cancelReply: "取消回复",
      deleteMessage: "删除消息",
      deleteConfirm: "确定删除此消息吗？此操作无法撤销。",
      messageDeleted: "消息已删除",
      unreadDivider: "未读消息",
      attachmentsPending: (count) => `${count} 个文件待发送`,
      addReaction: "添加表情回应",
      readBy: "已读成员",
      readReceiptsExpired: "已读记录仅保留 3 天",
      messageSent: "已发送",
      messageRead: "已读",
      stickers: "贴纸",
      stickersLoading: "正在加载贴纸...",
      loadOlder: "加载更早的消息",
    },
    mainChat: {
      title: "全部聊天",
      openLabel: "打开聊天",
      back: "全部聊天",
      loading: "正在加载聊天...",
      channelMain: "主聊天",
      channelDevelopers: "与开发者聊天",
      channelOnecSync: "1C 交换错误",
      openSyncFailed: "等待重试",
      sectionChannels: "频道",
      sectionReceipts: "委托单",
      noMessagesYet: "暂无消息",
      openReceipt: "打开委托单",
      unreadCount: (count) => `未读：${count}`,
      documentTitle: (count) => `(${count}) 消息`,
      live: "实时连接",
      connecting: "连接中…",
      offline: "离线",
      loadMore: "加载更早的会话",
    },
    backendUnavailable: "仓库后端服务不可用，请确认其已启动。",
  },
  pages: {
    back: "← 返回",
    title: "所有页面",
    subtitle: "应用的服务页面。",
    uiDescription: "按货运收据收货、发货和移库。",
    dbDescription: "直接编辑数据库表。",
    archDescription: "以图表展示所有流程和业务规则。",
    syncFailedDescription: "1C 多次拒绝、等待手动重试的对象。",
    credentialsDescription: "为新用户生成密码和消息。",
    syncQueueDescription: "下次交换时等待发送给 1C 的对象。",
    syncLogDescription: "1C 交换日志：何时收发了什么。",
    filesDescription: "S3 存储和 1C 中的所有文件：搜索、筛选、排序。",
    shippingDescription: "所有委托单的待发货单：选择一个车次的发货单，一键发货。",
  },
  tripShipping: {
    title: "车次发货",
    subtitle: "所有委托单的待发货单。勾选车次的发货单并一键发货：只要有一张发货单货物不足，就全部不发货。",
    back: "← 所有页面",
    refresh: "刷新",
    search: "发货单号、委托单号或货物",
    empty: "没有待发货单。",
    noMatch: "未找到。",
    selectDay: (date: string) => `选择 ${date} 的全部发货单`,
    places: "件",
    ordersCount: (count: number) => `${count} 张发货单`,
    selectAll: "全选",
    receipt: "委托单",
    orderLabel: "发货单",
    noDimensions: "未填尺寸",
    where: "库位",
    noStock: "无库存",
    viewByReceipt: "按委托单",
    viewByLocation: "按库位",
    scopeSelected: (count: number) => `所选发货单清单：${count}。`,
    scopeAll: (count: number) => `全部待发货单：${count}。在「按委托单」中勾选车次的发货单，清单将只包含它们。`,
    listTitle: "装车清单",
    copyText: "复制",
    copied: "已复制",
    print: "打印",
    volumeUnknown: "未计算体积：部分规格没有尺寸。",
    placesN: (count: number) => `${count} 件`,
    receiptMarked: "委托单已标记删除",
    readOnly: "只有仓管员和管理员可以发货。",
    selected: (count: number) => `已选发货单：${count}`,
    clear: "取消选择",
    ship: "车次发货",
    confirmTitle: "确认车次发货？",
    confirmText: (count: number) => `将以当前日期为 ${count} 张发货单过账出库。先检查库存：只要有一张货物不足，就全部不发货。`,
    confirm: "发货",
    checking: "正在检查库存…",
    progress: (done: number, total: number) => `已发货 ${done} / ${total}…`,
    blockedTitle: "车次未发货",
    blockedText: "未发货。货物不足：",
    shortage: (code: string, needPlaces: string, needKg: string, havePlaces: string, haveKg: string) =>
      `${code}：需要 ${needPlaces} 件（${needKg} 千克），库存 ${havePlaces} 件（${haveKg} 千克）`,
    together: "合计",
    doneTitle: "车次已发货",
    doneText: (count: number) => `已过账出库：${count}。`,
    stoppedTitle: "发货已停止",
    stoppedText: (order: string) => `发货单 ${order} 未发货，发货已停止：`,
    shipped: "已发货",
    left: "未发货",
    close: "关闭",
  },
  files: {
    title: "文件",
    subtitle: "聊天照片、委托单文件和 1C 文件。搜索、筛选和排序均在服务器端完成。",
    search: "按名称或委托单号搜索",
    source: "来源",
    sourceAll: "全部来源",
    sourceStorage: "S3 存储",
    sourceOnec: "1C",
    kind: "类型",
    kindAll: "全部类型",
    kinds: { image: "图片", pdf: "PDF", spreadsheet: "表格", document: "文档", other: "其他" },
    from: "从",
    to: "至",
    sort: "排序",
    sorts: {
      "created_at:desc": "最新优先",
      "created_at:asc": "最早优先",
      "name:asc": "名称 A→Z",
      "name:desc": "名称 Z→A",
      "size:desc": "最大优先",
      "size:asc": "最小优先",
    },
    view: "视图",
    viewGrid: "网格",
    viewTable: "表格",
    reset: "重置",
    name: "名称",
    receipt: "委托单",
    size: "大小",
    created: "添加时间",
    open: "打开",
    empty: "未找到文件。",
    shown: (n: number, total: number) => `显示 ${n} / ${total}`,
    totalSize: (size: string) => `总大小：${size}`,
    loadMore: "加载更多",
    refresh: "刷新",
    back: "← 所有页面",
  },
  credentials: {
    title: "密码生成器",
    subtitle: "生成密码、发给用户的消息以及 users.password_hash 的 bcrypt 哈希。全部在浏览器中计算。",
    login: "登录名",
    host: "地址",
    password: "密码",
    regenerate: "生成",
    message: "消息",
    copyMessage: "复制消息",
    hash: "bcrypt 哈希",
    hashing: "计算中…",
    hashHint: "创建用户时将哈希填入 password_hash。",
    copy: "复制",
    copied: "已复制",
  },
  sync: {
    back: "← 所有页面",
    failedTitle: "1C：等待重试",
    failedSubtitle: "三次失败后，对象不再自动发送给 1C。排除原因后点击“重试”。",
    queueTitle: "1C：交换队列",
    queueSubtitle: "1C 下次交换时将按此顺序取走的对象。1C 确认其版本后，对象离开队列。",
    emptyQueue: "队列为空——1C 已全部取走。",
    position: "序号",
    revision: "版本",
    logTitle: "1C：交换日志",
    logSubtitle: "最新在上。保留 90 天。",
    objectType: "类型",
    objectGuid: "GUID",
    errors: "错误数",
    error: "错误",
    enqueuedAt: "入队时间",
    retry: "重试",
    retried: "已重新入队",
    empty: "没有等待重试的对象。",
    emptyLog: "没有记录。",
    time: "时间",
    direction: "方向",
    action: "操作",
    result: "结果",
    ok: "成功",
    failed: "失败",
    inbound: "1C → 系统",
    outbound: "系统 → 1C",
    all: "全部",
    searchGuid: "按 GUID 搜索",
    loadMore: "加载更多",
    loading: "加载中…",
    refresh: "刷新",
    actions: { create: "创建", update: "更新", ack: "确认", fail: "1C 失败" } as Record<string, string>,
    types: {
      forwarding_orders: "货运收据",
      expected_arrivals: "预期到货",
      counterparties: "往来单位",
      warehouses: "仓库",
      files: "文件",
    } as Record<string, string>,
    ackRemoved: (rev: number) => `已从队列移除（版本 ${rev}）。`,
    ackStale: (sent: number, current: number) =>
      `未移除：版本不一致——1C 确认的是 ${sent}，队列中为 ${current}。请重新读取对象并确认队列给出的版本 ${current}。`,
    ackNotQueued: (rev: number) =>
      `未移除：对象不在队列中（版本 ${rev}）——之前已确认、从未入队，或类型 / GUID 错误。`,
    failCounted: (rev: number) => `1C 无法写入对象（版本 ${rev}）；已计入失败。`,
    failNotCounted: (rev: number) => `版本 ${rev} 的失败未计入：对象在读取后已更改或不在队列中。`,
    fieldsSent: "1C 发送的字段",
    sentGuid: "发送的 GUID",
    shown: (shown: number, total: number) => `显示 ${shown} / ${total}`,
  },
  arch: {
    title: "架构",
    subtitle: "用通俗的语言说明仓库和应用如何运作，面向第一次接触本项目的人。请从“仓库如何运作”开始阅读，不熟悉的词请查词汇表。图表为俄语。",
    renderError: "无法渲染图表。源代码：",
    viewer: {
      zoomIn: "放大",
      zoomOut: "缩小",
      fit: "适应窗口",
      fullscreen: "全屏",
      close: "关闭",
      hint: "拖动平移，Ctrl + 滚轮或双指缩放，双击放大",
      hintFullscreen: "滚轮或双指缩放，拖动平移，Esc 关闭",
    },
    howToRead: {
      title: "如何阅读图表",
      items: [
        "矩形——一个步骤或一条规则。箭头表示先后顺序。",
        "菱形——问题或分支；答案标在引出的箭头上。",
        "圆柱——数据存储的位置。",
        "虚线——说明或关联，而不是下一步。没有箭头的矩形是一条独立规则。",
        "大图可用上方的按钮放大或全屏查看。",
      ],
    },
    glossary: {
      title: "词汇表",
      description: "图表中出现的术语（俄语）。",
    },
    groups: {
      business: { title: "仓库如何运作", description: "业务流程：货物经历什么、遵循哪些规则。请按顺序阅读。" },
      technical: { title: "应用如何构建", description: "面向开发人员和管理员：系统由哪些部分组成、它们如何通信。" },
    },
    sections: {
      overview: { title: "概览：货物的旅程", description: "一张图看完整个流程，从订单到发货。从这里开始。" },
      lifecycle: { title: "收据状态", description: "一批货物会处于哪些状态，以及什么使它从一个状态进入下一个。" },
      receiving: { title: "收货", description: "货物到达时发生什么，系统如何判断是全部到达还是部分到达。" },
      operations: { title: "发货、报废、移库", description: "货物如何离开仓库或更换库位，以及系统事先检查什么。" },
      shipping: { title: "发运单与出库", description: "1C 如何用发运单组建车次，仓管员如何整单发运。" },
      ledger: { title: "库存如何计算", description: "库存不以数字存储，而是根据入库和出库记录计算。操作不能删除。" },
      fact: { title: "货物实际数据", description: "货物的真实数量：在库时为库中现有，发货后为已发出的部分。" },
      masterdata: { title: "收据、包装规格与基础数据", description: "编号如何分配、哪些不能删除、计划从何而来。" },
      ui: { title: "/ui 界面", description: "用户看到的内容：首页标签、收据卡片及其标签页。" },
      reports: { title: "列表与报表", description: "“在库”“待到货”、收据卡片和搜索中的数字从何而来。" },
      files: { title: "文件", description: "照片和文件：在应用中上传的或由 1C 发送的。" },
      chat: { title: "聊天", description: "每张收据的聊天、公共频道和消息规则。" },
      dataflow: { title: "数据流", description: "数据来自哪里、如何校验、存储在哪里、谁能看到。每个数据流是一条彩色泳道，从左向右阅读；«→ ③» 标记表示“在数据流 ③ 中继续”，在那里标为 «из ②»（来自 ②）。" },
      system: { title: "系统概览", description: "应用由哪些部分组成，谁与谁通信。" },
      auth: { title: "登录与权限", description: "登录如何进行、有效多久、各角色可以做什么。" },
      onec: { title: "1C 数据交换", description: "应用与 1C 如何交换数据：先由 1C 取走本系统的变更，再发送它自己的变更。" },
      onecqueue: { title: "发往 1C 的队列", description: "已更改的收据在 1C 取走之前经历什么，出错时怎么办。" },
      realtime: { title: "即时消息", description: "聊天和“在线用户”列表如何在不刷新页面的情况下更新。" },
    },
  },
};

export default zh;
